import { findBookmarkName } from "@via/pages/data/file-organizer/helpers.ts";
import { FileOrganizerProvider } from "@via/context/file-organizer-context.tsx";
import { useAmazonS3Connection } from "@via/hooks/useAmazonS3Connection.tsx";
import NoActiveConnection from "@via/pages/data/data-sources/cloud-data-sources/common/no-active-connection.tsx";
import DataSourceTitle from "@via/pages/data/data-sources/data-source-title.tsx";
import FileOrganizer from "@via/pages/data/file-organizer/file-organizer.tsx";
import { DataSourceComponentView } from "@via/utils/interfaces.tsx";
import { DataSourceProvider, type SelectedFile } from "common";
import { type JSX, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import S3Logo from "/aws-s3-logo.svg";
import AmazonFolderBrowser from "@via/pages/data/data-sources/cloud-data-sources/amazon-s3/amazon-folder-browser.tsx";
import { useGetDataSourceList } from "@via/pages/data/data-connections/data-connections.api.tsx";
import { prepareBucketPath } from "@via/pages/data/data-sources/data-source.api.tsx";
import { BucketObject } from "@via/pages/data/data-sources/utils/helpers.tsx";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";

function AmazonS3(): JSX.Element {
  const [searchParams] = useSearchParams();
  const bookmarkId = searchParams.get("bookmark");
  const { data: bookmarkList } = useGetDataSourceList();

  useEffect(() => {
    if (bookmarkId && bookmarkList?.length) {
      const selectedDatasource = bookmarkList.find(
        item => item.id === Number(bookmarkId)
      );

      const bookmarkCredentials = {
        savedCredentialId: selectedDatasource?.credentialId,
        bucketName: selectedDatasource?.path || "",
      };
      prepareBucketPath(bookmarkCredentials);
      setConnectionCredentials(bookmarkCredentials);
    }
  }, [bookmarkId, bookmarkList]);

  const [currentView, setCurrentView] = useState<DataSourceComponentView>(
    DataSourceComponentView.FolderBrowser
  );
  const [hasConnection, setHasConnection] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [globalBucketName, setGlobalBucketName] = useState("");
  const [cart, setCart] = useState<BucketObject[]>([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const updateSelectedFiles = (selectedFileList: SelectedFile[]): void => {
    setGlobalBucketName(
      connectionCredentials?.bucketName
        ? `s3://${connectionCredentials?.bucketName}`
        : ""
    );
    setSelectedFiles(
      selectedFileList.map(file => ({
        ...file,
        id: file.name,
      }))
    );
    setCurrentView(DataSourceComponentView.FileOrganizer);
  };

  const setCurrentViewToFolderBrowser = () => {
    setCurrentView(DataSourceComponentView.FolderBrowser);
  };

  const { connectionCredentials, setConnectionCredentials } =
    useAmazonS3Connection();

  useEffect(() => {
    if (connectionCredentials) {
      setHasConnection(true);
      setCurrentView(DataSourceComponentView.FolderBrowser);
    }
  }, [connectionCredentials]);

  const uploadButton =
    hasConnection && currentView === DataSourceComponentView.FolderBrowser ? (
      <button
        data-testid="folderBrowserUploadButton"
        onClick={() => setUploadModalOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border border-indigo-600 bg-white px-3 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
        <ArrowUpTrayIcon className="h-5 w-5" />
        Upload File(s)
      </button>
    ) : null;

  return (
    <div className="flex-auto">
      {
        <div className="ml-2">
          <DataSourceTitle
            logoPath={S3Logo}
            title="AWS S3"
            actions={uploadButton}
          />
          {currentView === DataSourceComponentView.FileOrganizer ? (
            <FileOrganizerProvider>
              <FileOrganizer
                importedFiles={selectedFiles}
                globalBucketName={globalBucketName}
                onBackToSelectionClick={setCurrentViewToFolderBrowser}
                credentialId={connectionCredentials?.savedCredentialId || null}
                dataSourceId={bookmarkId ? Number(bookmarkId) : undefined}
                dataSourceName={findBookmarkName(bookmarkList, bookmarkId)}
                provider={DataSourceProvider.Amazon}
              />
            </FileOrganizerProvider>
          ) : (
            <div>
              {hasConnection ? (
                <AmazonFolderBrowser
                  onContinue={updateSelectedFiles}
                  cart={cart}
                  setCart={setCart}
                  uploadModalOpen={uploadModalOpen}
                  setUploadModalOpen={setUploadModalOpen}
                />
              ) : (
                <NoActiveConnection />
              )}
            </div>
          )}
        </div>
      }
    </div>
  );
}

/**
 * AmazonS3Wrapper Component
 *
 *  * Route Definition
 *  *
 *  * This route definition uses the `AmazonS3Wrapper` component to ensure that the
 *  * `AmazonS3` component is re-rendered with a fresh instance whenever the URL changes.
 *
 * This wrapper component extracts the `dataSourceName` parameter from the URL
 * using the `useParams` hook and passes it as a unique `key` prop to the
 * `AmazonS3` component. By using the `key` prop, we ensure that React treats
 * each unique `dataSourceName` as a separate instance of the `AmazonS3` component.
 *
 * This approach forces React to unmount the current `AmazonS3` component instance
 * and mount a new one whenever the `dataSourceName` changes. This effectively
 * resets the component's state and lifecycle, ensuring that the `AmazonS3`
 * component is recreated with fresh state every time the route parameter changes.
 */
export const AmazonS3Wrapper = () => {
  const [searchParams] = useSearchParams();
  const bookmarkId = searchParams.get("bookmark") || "";
  return <AmazonS3 key={bookmarkId} />;
};
